import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Pagination,
  Box,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { toast } from 'react-toastify';
import AssignPatientDialog from './AssignPatientDialog';

const AidantTable = ({
  loading,
  currentData,
  currentPage,
  count,
  apiUrl,
  token,
  onView,
  onEdit,
  onDelete,
  onPageChange,
  onPatientAssigned,
}) => {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedAidantForAssign, setSelectedAidantForAssign] = useState(null);

  // État pour gérer le dialogue d'affichage/désaffectation des patients depuis le tableau
  const [managePatientsOpen, setManagePatientsOpen] = useState(false);
  const [selectedAidantForManage, setSelectedAidantForManage] = useState(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress sx={{ color: '#4318FF' }} />
      </Box>
    );
  }

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    return `${apiUrl.replace('/api', '')}/storage/${photoPath}`;
  };

  const handleOpenAssignDialog = (aidant) => {
    setSelectedAidantForAssign(aidant);
    setAssignDialogOpen(true);
  };

  const handleOpenManagePatients = (aidant) => {
    setSelectedAidantForManage(aidant);
    setManagePatientsOpen(true);
  };

  // Fonction pour désaffecter un patient directement depuis le tableau
  const handleDetachPatientFromTable = async (patientId, patientName) => {
    if (window.confirm(`Voulez-vous vraiment désaffecter le patient ${patientName} de cet aidant ?`)) {
      try {
        await axios.delete(
          `${apiUrl}/admin/aidants/${selectedAidantForManage.id_aidant}/patients/${patientId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success('Patient désaffecté avec succès');

        // Mettre à jour l'objet localement pour rafraîchir le dialogue instantanément
        const updatedPatients = selectedAidantForManage.patients.filter(
          (p) => p.id_patient_profile !== patientId
        );
        setSelectedAidantForManage({
          ...selectedAidantForManage,
          patients: updatedPatients,
        });

        // Rafraîchir la liste générale du parent
        if (onPatientAssigned) onPatientAssigned();
      } catch (err) {
        console.error('Erreur lors de la désaffectation :', err);
        toast.error('Erreur lors de la désaffectation du patient');
      }
    }
  };

  return (
    <Box>
      <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: '0px 4px 12px rgba(0,0,0,0.03)' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#F4F7FE' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: '#A3AED0' }}>Photo</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#A3AED0' }}>Nom & Prénom</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#A3AED0' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#A3AED0' }}>Téléphone</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#A3AED0' }}>Ville</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#A3AED0' }}>Patients rattachés</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#A3AED0' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentData.length > 0 ? (
              currentData.map((aidant) => {
                const patientsCount = aidant.patients ? aidant.patients.length : 0;
                
                return (
                  <TableRow key={aidant.id_aidant} sx={{ '&:hover': { bgcolor: '#FAFCFE' } }}>
                    <TableCell>
                      <Avatar
                        src={getPhotoUrl(aidant.photo_aidant)}
                        alt={`${aidant.nom_aidant} ${aidant.prenom_aidant}`}
                        sx={{ width: 40, height: 40, bgcolor: '#4318FF' }}
                      >
                        {aidant.nom_aidant?.charAt(0)}
                      </Avatar>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2B3674' }}>
                      {aidant.nom_aidant} {aidant.prenom_aidant}
                    </TableCell>
                    <TableCell>{aidant.email_aidant}</TableCell>
                    <TableCell>{aidant.telephone_aidant || 'Non renseigné'}</TableCell>
                    <TableCell>{aidant.ville_aidant || 'Non renseignée'}</TableCell>
                    
                    {/* Colonne Patients Rattachés cliquable pour gérer */}
                    <TableCell>
                      {patientsCount > 0 ? (
                        <Tooltip title="Cliquer pour voir et gérer les patients rattachés" arrow>
                          <Chip
                            icon={<PersonIcon />}
                            label={`${patientsCount} patient${patientsCount > 1 ? 's' : ''}`}
                            color="primary"
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenManagePatients(aidant)}
                            sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { backgroundColor: '#EEF2FF' } }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          label="Aucun patient"
                          size="small"
                          sx={{ bgcolor: '#F4F7FE', color: '#A3AED0', fontWeight: 500 }}
                        />
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center">
                      <Tooltip title="Affecter un patient">
                        <IconButton 
                          onClick={() => handleOpenAssignDialog(aidant)} 
                          sx={{ color: '#2B3674' }}
                        >
                          <PersonAddIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Voir">
                        <IconButton onClick={() => onView(aidant)} sx={{ color: '#4318FF' }}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Modifier">
                        <IconButton onClick={() => onEdit(aidant)} color="secondary">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Supprimer">
                        <IconButton onClick={() => onDelete(aidant)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">Aucun aidant trouvé.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {count > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={count}
            page={currentPage}
            onChange={(e, value) => onPageChange(value)}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: '#4318FF',
                color: '#fff',
              },
            }}
          />
        </Box>
      )}

      {/* Dialogue d'affectation d'un patient */}
      {selectedAidantForAssign && (
        <AssignPatientDialog
          open={assignDialogOpen}
          aidantId={selectedAidantForAssign.id_aidant}
          apiUrl={apiUrl}
          token={token}
          onClose={() => {
            setAssignDialogOpen(false);
            setSelectedAidantForAssign(null);
          }}
          onPatientAssigned={() => {
            if (onPatientAssigned) onPatientAssigned();
          }}
        />
      )}

      {/* Dialogue de gestion/désaffectation rapide depuis le badge du tableau */}
      {selectedAidantForManage && (
        <Dialog
          open={managePatientsOpen}
          onClose={() => setManagePatientsOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 'bold', color: '#2B3674' }}>
            Patients rattachés à {selectedAidantForManage.nom_aidant} {selectedAidantForManage.prenom_aidant}
          </DialogTitle>
         <DialogContent dividers>
  {selectedAidantForManage.patients && selectedAidantForManage.patients.length > 0 ? (
    <Stack spacing={1.5}>
      {selectedAidantForManage.patients.map((patient, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: '#FAFCFE',
            p: 1.5,
            borderRadius: '8px',
            border: '1px solid #E0E5F2',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PersonIcon sx={{ color: '#4318FF' }} />
            <Box>
              <Typography variant="body2" fontWeight={600} color="#2B3674">
                {patient.nom_patient} {patient.prenom_patient}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                ID: {patient.id_patient_profile} | Tél: {patient.telephone_patient || 'Non renseigné'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* 👈 Titre + Chip du lien patient */}
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                Lien patient
              </Typography>
              <Chip
                label={patient.pivot?.lien_patient_aidant || 'Lien non défini'}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: '0.75rem', height: '24px' }}
              />
            </Box>

            <Tooltip title="Désaffecter ce patient">
              <IconButton
                size="small"
                color="error"
                onClick={() =>
                  handleDetachPatientFromTable(
                    patient.id_patient_profile,
                    `${patient.nom_patient} ${patient.prenom_patient}`
                  )
                }
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ))}
    </Stack>
  ) : (
    <Typography align="center" color="textSecondary" sx={{ py: 2 }}>
      Aucun patient rattaché pour le moment.
    </Typography>
  )}
</DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setManagePatientsOpen(false)}
              variant="contained"
              sx={{ bgcolor: '#4318FF', '&:hover': { bgcolor: '#3311CC' } }}
            >
              Fermer
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default AidantTable;